import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const CATS = [
  { key: "verbal", label: "Verbale" },
  { key: "numerical", label: "Numerico" },
  { key: "abstract", label: "Astratto" },
];

export default function AdminPreview() {
  const { user } = useAuth();
  const [cat, setCat] = useState("verbal");
  const [data, setData] = useState(null);
  const [onlyPremium, setOnlyPremium] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/admin/questions/${cat}`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [cat]);

  if (user && user.role !== "admin") {
    return <Layout><div className="max-w-4xl mx-auto p-10 text-slate-600" data-testid="admin-denied">Accesso riservato all'amministratore.</div></Layout>;
  }

  const list = data ? (onlyPremium ? data.questions.filter((q) => !q.is_free) : data.questions) : [];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="label-mono text-[#002FA7] mb-2">— Anteprima amministratore</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Contenuti protetti da paywall</h1>
        <p className="text-slate-600 mb-8">Rilettura completa delle domande (gratuite e premium) con risposta corretta e spiegazione. Solo per controllo interno.</p>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          {CATS.map((c) => (
            <button key={c.key} data-testid={`admin-tab-${c.key}`} onClick={() => setCat(c.key)}
              className={`label-mono px-4 py-2 border ${cat === c.key ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}>
              {c.label}
            </button>
          ))}
          <label className="ml-4 flex items-center gap-2 label-mono text-slate-600 cursor-pointer">
            <input type="checkbox" className="accent-[#002FA7]" checked={onlyPremium} onChange={(e) => setOnlyPremium(e.target.checked)} data-testid="admin-only-premium" />
            solo premium
          </label>
          {data && <span className="label-mono text-slate-400 ml-auto">{data.total} totali · {data.free} gratuite</span>}
        </div>

        {!data && <div className="text-slate-500">Caricamento…</div>}

        <div className="space-y-4" data-testid="admin-questions-list">
          {list.map((q, i) => (
            <div key={q.id} data-testid={`admin-q-${q.id}`} className="border border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="label-mono text-slate-500">{q.id}</span>
                <span className={`label-mono text-xs px-2 py-1 ${q.is_free ? "bg-[#FFD700] text-[#0A0F1A]" : "bg-[#0A0F1A] text-white"}`}>
                  {q.is_free ? "GRATIS" : "PREMIUM"}
                </span>
              </div>
              <p className="whitespace-pre-line text-sm text-slate-800 mb-4">{q.prompt}</p>

              {q.table && (
                <div className="border border-slate-300 mb-4 overflow-x-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-slate-100 label-mono">
                      <tr>{q.table.headers.map((h, j) => (<th key={j} className="text-left p-2 border-r last:border-r-0 border-slate-300">{h}</th>))}</tr>
                    </thead>
                    <tbody>
                      {q.table.rows.map((row, ri) => (
                        <tr key={ri} className="border-t border-slate-300">
                          {row.map((cell, ci) => (<td key={ci} className="p-2 border-r last:border-r-0 border-slate-200 font-mono-num">{cell}</td>))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {q.matrix && (
                <div className="mb-4 grid grid-cols-3 border border-slate-400" style={{ width: 240 }}>
                  {q.matrix.map((cell, j) => (
                    <div key={j} className={`aspect-square border border-slate-300 ${cell ? "bg-white" : "bg-slate-50 flex items-center justify-center"}`}>
                      {cell ? <div className="w-full h-full p-1" dangerouslySetInnerHTML={{ __html: cell }} /> : <span className="text-xl font-black text-[#002FA7]">?</span>}
                    </div>
                  ))}
                </div>
              )}

              <div className={q.matrix ? "grid grid-cols-2 md:grid-cols-4 gap-2" : "space-y-2"}>
                {Object.entries(q.options).map(([k, v]) => {
                  const ok = k === q.correct;
                  const isSvg = typeof v === "string" && v.trim().startsWith("<svg");
                  return (
                    <div key={k} className={`border p-2 text-sm flex items-center gap-2 ${ok ? "border-green-500 bg-green-50" : "border-slate-200"}`}>
                      {ok && <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />}
                      <span className="label-mono text-[#002FA7]">{k}</span>
                      {isSvg ? <div className="w-12 h-12" dangerouslySetInnerHTML={{ __html: v }} /> : <span>{v}</span>}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-200 text-sm text-slate-600">
                <span className="label-mono text-slate-400">Corretta: </span><span className="font-bold text-[#002FA7]">{q.correct}</span>
                {q.explanation && <div className="mt-1">{q.explanation}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
